import type {
  Follow,
  StopLoader,
  PaginatedLoader,
  PaginatedLoadingContextValue,
  LoadedRange,
} from "@tandemdiabetes/sugarmate-api-react-client"
import { createPaginatedLoadingContextAndProvider } from "@tandemdiabetes/sugarmate-api-react-client"
import dayjs from "dayjs"
import type {
  FC,
  ReactNode,
} from "react"
import {
  useCallback,
  useEffect,
  useMemo,
} from "react"
import type { RxCollection } from "rxdb"
import { useRxCollection } from "rxdb-hooks"
import type { ReloadState } from "./createAggregateReloadingContextProvider"
import { useFollowedUsers } from "src/contexts/FollowedUsersContext"
import { usePageLifecycleContext } from "src/contexts/PageLifecycleContext"
import { useRefValue } from "src/hooks/utils"
import type { CollectionKey } from "src/types"
import { PageLifecycle } from "src/types"

type GetLoadedRangesForAccountId = (
  accountId: Follow["following_account_id"],
  defaultValue: LoadedRange[],
) => Promise<LoadedRange[]>

type SetLoadedRangesForAccountId = (
  accountId: Follow["following_account_id"],
  loadedRanges: LoadedRange[],
) => Promise<void>

interface Props {
  followId: Follow["id"];
  accountId: Follow["following_account_id"];
  getLoadedRangesForAccountId: GetLoadedRangesForAccountId;
  setLoadedRangesForAccountId: SetLoadedRangesForAccountId;
  useReloadingContextHook: () => ReloadState;
  children?: ReactNode;
}

/*
 * this function returns a provider for the loading context of a specific
 * model and a cook for consuming that context
 *
 * the provider handles executing the loader returned by "useLoader" at
 * the appropriate time
 */
export function createPaginatedLoadingContextProvider<T>(
  collectionKey: CollectionKey,
  useLoader: () => [
    PaginatedLoadingContextValue,
    PaginatedLoader<T>,
    StopLoader,
  ],
): [FC<Props>, () => PaginatedLoadingContextValue] {
  const [
    useLoadingContext,
    LoadingContextProvider,
  ] = createPaginatedLoadingContextAndProvider()

  const LoadingProvider = (props: Props) => {
    const {
      followId,
      accountId,
      getLoadedRangesForAccountId,
      setLoadedRangesForAccountId,
      useReloadingContextHook,
      children,
    } = props

    const rxCollection = useRxCollection(collectionKey) as RxCollection<T>
    const { currentUserId } = useFollowedUsers()
    const pageLifecycle = usePageLifecycleContext()

    const upsertItems = useMemo(
      () => !rxCollection
        ? undefined
        : async (items: T[]) => {
          try {
            const response = await rxCollection.bulkUpsert(items)
            return response
          } catch (e) {
            if (process.env.NODE_ENV === "development") {
              console.log(
                `failed to insert ${items.length} items into ${collectionKey}`,
              )
            }
            throw e
          }
        },
      [rxCollection],
    )

    const [
      loadingState,
      loader,
      stopLoader,
    ] = useLoader()

    const isVisible = pageLifecycle === PageLifecycle.Active || PageLifecycle.Passive

    const callLoader = useCallback(
      async () => {
        if (loader && upsertItems && isVisible) {
          const after = dayjs()
            .subtract(
              90,
              "days",
            )
            .startOf("day")
            .toISOString()

          try {
            const getLoadedRanges = (defaultValue: LoadedRange[]) => (
              getLoadedRangesForAccountId(
                accountId,
                defaultValue,
              )
            )

            const setLoadedRanges = (loadedRanges: LoadedRange[]) => (
              setLoadedRangesForAccountId(
                accountId,
                loadedRanges,
              )
            )

            await loader(
              upsertItems,
              getLoadedRanges,
              setLoadedRanges,
              {
                followId,
                after,
              },
            )
          } catch (e) {
            if (process.env.NODE_ENV === "development") {
              console.log(`loading ${collectionKey} failed for ${accountId}`)
              console.log(e)
            }
          }
        }
      },
      [
        loader,
        upsertItems,
        isVisible,
        followId,
        accountId,
        getLoadedRangesForAccountId,
        setLoadedRangesForAccountId,
      ],
    )

    const callLoaderRef = useRefValue(callLoader)

    const isCurrentUser = accountId === currentUserId

    useEffect(
      () => {
        if (isCurrentUser) {
          // for the current user call the loader immediately
          void callLoader()

          return () => {
            stopLoader()
          }
        }
      },
      [
        isCurrentUser,
        callLoader,
        stopLoader,
      ],
    )

    const { reloadRequests } = useReloadingContextHook()
    const reloadRequest: string | undefined = reloadRequests[followId]

    // execute the loader if a reload is requested
    useEffect(
      () => {
        if (reloadRequest) {
          void callLoaderRef.current()
        }
      },
      [
        reloadRequest,
        callLoaderRef,
      ],
    )

    return (
      <LoadingContextProvider {...loadingState}>
        {children}
      </LoadingContextProvider>
    )
  }

  return [
    LoadingProvider,
    useLoadingContext,
  ]
}
